// Post Detail Page
// Full post view with comments

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Post, Comment } from '../../types/community';
import { CommunityService } from '../../services/Community/communityService';
import { PostCard } from '../../components/CommunityChatRooms/PostCard';
import { CommentCard } from '../../components/CommunityChatRooms/CommentCard';
import { RefreshButton } from '../../components/CommunityChatRooms/RefreshButton';
import { useChatRoomTheme } from '../../contexts/ChatRoomThemeContext';
import { signalsService } from '../../services/SocialFeed/signalsService';
import { checkLinkPolicy, roomRestrictsLinks } from '../../utils/communityLinkPolicy';
import { EmojiPicker } from '../../components/CommunityChatRooms/EmojiPicker';
import {
  PostDetailContainer,
  PostDetailBackButton,
  PostSection,
  CommentsSection,
  CommentsHeader,
  CommentsTitle,
  CommentsList,
  AddCommentSection,
  CommentInput,
  SubmitButton,
} from '../../styles/CommunityChatRooms/PostDetail.styles';
import { ChatRoomToast } from '../../styles/CommunityChatRooms/CommunityFeed.styles';
import { ProfilePeekDrawer, useProfilePeek } from '../../components/shared/ProfilePeekDrawer';

export const PostDetail: React.FC = () => {
  const { personaId, roomId, postId } = useParams<{
    personaId?: string;
    roomId?: string;
    postId: string;
  }>();
  const navigate = useNavigate();
  const { theme } = useChatRoomTheme();
  // Same drawer as the room feed, so a name opens a profile identically
  // whether it is on a post or a comment.
  const { peekTarget, openPeek, closePeek, toast, showToast } = useProfilePeek('communities');
  const openAuthor = (authorId: string, authorName: string) =>
    openPeek({ profileId: authorId, firstName: authorName, context: 'From this chat room' });
  const communityId = personaId || roomId || '';

  const [post, setPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentBody, setCommentBody] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);

  // Inserted at the caret so an emoji lands where the writer is typing.
  const insertEmoji = (emoji: string) => {
    const textarea = commentRef.current;
    if (!textarea) {
      setCommentBody((prev) => prev + emoji);
      return;
    }
    const start = textarea.selectionStart ?? commentBody.length;
    const end = textarea.selectionEnd ?? commentBody.length;
    setCommentBody(commentBody.slice(0, start) + emoji + commentBody.slice(end));
    requestAnimationFrame(() => {
      textarea.focus();
      const caret = start + emoji.length;
      textarea.setSelectionRange(caret, caret);
    });
  };

  useEffect(() => {
    loadPost();
    loadComments();
  }, [postId, communityId]);

  const loadPost = async () => {
    try {
      setIsLoading(true);
      const fetchedPost = await CommunityService.getPost(postId!, communityId);
      setPost(fetchedPost);
    } catch (error) {
      console.error('Error loading post:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const fetchedComments = await CommunityService.getPostComments(postId!);
      setComments(fetchedComments);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const handleBack = () => {
    if (personaId) {
      navigate(`/community-chat-rooms/${personaId}`);
    } else if (roomId) {
      navigate(`/community-chat-rooms/room/${roomId}`);
    } else {
      navigate('/community-chat-rooms');
    }
  };

  const handleAddComment = async () => {
    if (!commentBody.trim() || isSubmitting) return;

    // A comment carries a link as easily as a post does, so the room's link
    // rules apply here too. The server checks again and has the last word.
    const violation = checkLinkPolicy(communityId, commentBody);
    if (violation) {
      setCommentError(violation);
      return;
    }

    setCommentError(null);
    setIsSubmitting(true);
    try {
      const newComment = await CommunityService.createComment(postId!, commentBody.trim());
      // Commenting on someone's post is engagement with that person, not just
      // with the room.
      if (post?.authorId) {
        signalsService.track({
          signalType: 'comment_create',
          targetUserId: post.authorId,
          surface: 'communities',
          itemId: postId,
        });
      }
      setComments((prev) => [newComment, ...prev]);
      setCommentBody('');
      // Update post comment count
      if (post) {
        setPost({ ...post, commentCount: post.commentCount + 1 });
      }
    } catch (error: any) {
      // Surfaced rather than only logged: a rejected comment used to leave the
      // text in the box with no explanation.
      console.error('Error creating comment:', error);
      setCommentError(error?.message || 'Could not post that comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddReply = async (parentCommentId: string, body: string) => {
    const newReply = await CommunityService.createComment(postId!, body, parentCommentId);
    setComments((prev) => [...prev, newReply]);
    if (post) {
      setPost({ ...post, commentCount: post.commentCount + 1 });
    }
  };

  const handleVote = async (itemId: string, voteType: 'upvote' | 'downvote', itemType: 'post' | 'comment') => {
    try {
      if (itemType === 'post') {
        await CommunityService.voteOnPost(itemId, voteType);
        if (post?.authorId) {
          signalsService.track({
            signalType: voteType === 'upvote' ? 'post_upvote' : 'post_downvote',
            targetUserId: post.authorId,
            surface: 'communities',
            itemId,
          });
        }
        // Update post vote
        if (post && post.id === itemId) {
          const wasUpvoted = post.userVote === 'upvote';
          const wasDownvoted = post.userVote === 'downvote';
          const isUpvoting = voteType === 'upvote';

          let newVote: 'upvote' | 'downvote' | null = null;
          let upvotes = post.upvotes;
          let downvotes = post.downvotes;

          if (isUpvoting) {
            if (wasUpvoted) {
              newVote = null;
              upvotes -= 1;
            } else {
              newVote = 'upvote';
              upvotes += 1;
              if (wasDownvoted) downvotes -= 1;
            }
          } else {
            if (wasDownvoted) {
              newVote = null;
              downvotes -= 1;
            } else {
              newVote = 'downvote';
              downvotes += 1;
              if (wasUpvoted) upvotes -= 1;
            }
          }

          setPost({ ...post, userVote: newVote, upvotes, downvotes });
        }
      } else {
        await CommunityService.voteOnComment(itemId, voteType);
        const votedComment = comments.find((c) => c.id === itemId);
        if (votedComment?.authorId) {
          signalsService.track({
            signalType: voteType === 'upvote' ? 'comment_upvote' : 'comment_downvote',
            targetUserId: votedComment.authorId,
            surface: 'communities',
            itemId,
          });
        }
        // Update comment vote
        setComments((prev) =>
          prev.map((comment) => {
            if (comment.id === itemId) {
              const wasUpvoted = comment.userVote === 'upvote';
              const wasDownvoted = comment.userVote === 'downvote';
              const isUpvoting = voteType === 'upvote';

              let newVote: 'upvote' | 'downvote' | null = null;
              let upvotes = comment.upvotes;
              let downvotes = comment.downvotes;

              if (isUpvoting) {
                if (wasUpvoted) {
                  newVote = null;
                  upvotes -= 1;
                } else {
                  newVote = 'upvote';
                  upvotes += 1;
                  if (wasDownvoted) downvotes -= 1;
                }
              } else {
                if (wasDownvoted) {
                  newVote = null;
                  downvotes -= 1;
                } else {
                  newVote = 'downvote';
                  downvotes += 1;
                  if (wasUpvoted) upvotes -= 1;
                }
              }

              return { ...comment, userVote: newVote, upvotes, downvotes };
            }
            return comment;
          })
        );
      }
    } catch (error) {
      console.error('Error voting:', error);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadPost();
    loadComments();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  if (isLoading) {
    return (
      <PostDetailContainer $theme={theme}>
        <div style={{ textAlign: 'center', padding: '3rem', color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)' }}>
          Loading post...
        </div>
      </PostDetailContainer>
    );
  }

  if (!post) {
    return (
      <PostDetailContainer $theme={theme}>
        <PostDetailBackButton $theme={theme} onClick={handleBack}>← Back</PostDetailBackButton>
        <div style={{ textAlign: 'center', padding: '3rem', color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)' }}>
          Post not found
        </div>
      </PostDetailContainer>
    );
  }

  return (
    <PostDetailContainer $theme={theme}>
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', padding: '0 1.25rem', marginBottom: '1rem' }}>
        <PostDetailBackButton $theme={theme} onClick={handleBack}>← Back</PostDetailBackButton>
      </div>

      <PostSection>
        <PostCard
          post={post}
          onClick={() => { }} // No navigation needed, already on detail page
          onVote={(postId, voteType) => handleVote(postId, voteType, 'post')}
          onSave={async (postId) => {
            await CommunityService.savePost(postId);
            setPost({ ...post, isSaved: !post.isSaved });
          }}
          showFullBody={true}
          onAuthorClick={openAuthor}
        />
      </PostSection>

      <CommentsSection>
        <CommentsHeader $theme={theme}>
          <CommentsTitle $theme={theme}>
            {comments.length} {comments.length === 1 ? 'Comment' : 'Comments'}
          </CommentsTitle>
          <RefreshButton
            onClick={handleRefresh}
            disabled={isRefreshing}
            isRefreshing={isRefreshing}
          />
        </CommentsHeader>

        <AddCommentSection $theme={theme}>
          <CommentInput
            ref={commentRef}
            $theme={theme}
            value={commentBody}
            onChange={(e) => setCommentBody(e.target.value)}
            placeholder="What are your thoughts? Paste a GIF link to reply with a GIF"
            rows={4}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.4rem' }}>
            <EmojiPicker theme={theme} onSelect={insertEmoji} label="Add an emoji to your comment" />
            <span style={{ fontSize: '0.8rem', opacity: 0.65 }}>
              Paste a GIF link to reply with a GIF.
            </span>
          </div>
          {roomRestrictsLinks(communityId) && !commentError && (
            <div style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.4rem' }}>
              Only LinkedIn links can be shared in this room.
            </div>
          )}
          {commentError && (
            <div role="alert" style={{ fontSize: '0.85rem', color: '#ef4444', marginTop: '0.4rem' }}>
              {commentError}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <SubmitButton
              onClick={handleAddComment}
              disabled={!commentBody.trim() || isSubmitting}
            >
              {isSubmitting ? 'Posting...' : 'Post Comment'}
            </SubmitButton>
          </div>
        </AddCommentSection>

        <CommentsList>
          {comments.length > 0 ? (
            comments
              .filter((comment) => !comment.parentCommentId)
              .map((comment) => (
                <CommentCard
                  key={comment.id}
                  comment={comment}
                  replies={comments.filter((c) => c.parentCommentId === comment.id)}
                  onVote={(commentId, voteType) => handleVote(commentId, voteType, 'comment')}
                  onReply={handleAddReply}
                  onAuthorClick={openAuthor}
                />
              ))
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: theme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)' }}>
              No comments yet. Be the first to comment!
            </div>
          )}
        </CommentsList>
      </CommentsSection>

      <ProfilePeekDrawer
        target={peekTarget}
        onClose={closePeek}
        backLabel="Post"
        onNotify={(_type, message) => showToast(message)}
      />
      {toast && <ChatRoomToast $theme={theme}>{toast}</ChatRoomToast>}
    </PostDetailContainer>
  );
};

